"use client";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Edit2 } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { XIcon } from "../XIcon";

interface SortableImageItemProps {
  id: string;
  file: File;
  previewUrl: string | undefined;
  icon: ReactNode;
  onDelete: () => void;
  showDeleteButton: boolean;
  onShowDeleteButton: () => void;
  onEdit?: () => void;
}

export const SortableImageItem = ({
  id,
  file,
  previewUrl,
  icon,
  onDelete,
  showDeleteButton,
  onShowDeleteButton,
  onEdit,
}: SortableImageItemProps) => {
  const t = useTranslations("common.form");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Handle click to show delete button (onClick doesn't fire during drag)
  const handleContainerClick = () => {
    // Check if it's a touch device
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
      onShowDeleteButton();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative flex flex-col gap-2 group",
        isDragging && "opacity-50 z-50",
      )}
    >
      {/* Image container - this is the drag handle */}
      <div
        className="relative w-[174px] h-[174px] rounded-sm overflow-hidden cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
        onClick={handleContainerClick}
      >
        {previewUrl ? (
          // biome-ignore lint: img tag needed for blob URL previews
          <img
            src={previewUrl}
            alt={file.name}
            className="w-full h-full object-cover pointer-events-none"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full bg-bg-secondary flex items-center justify-center pointer-events-none">
            {icon}
          </div>
        )}
      </div>

      {/* Action buttons container */}
      <div
        className={cn(
          "absolute top-1 right-1 flex items-center gap-1 z-10",
          // Desktop: show on hover
          "opacity-0 pointer-events-none",
          "group-hover:opacity-100 group-hover:pointer-events-auto",
          // Mobile: show when clicked/touched (stays for 10 seconds)
          showDeleteButton && "opacity-100 pointer-events-auto",
        )}
      >
        {/* Edit button - only shown for images with onEdit callback */}
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit();
            }}
            className={cn(
              "inline-flex items-center justify-center size-7 text-text-primary",
              "transition-all duration-200 bg-bg-tertiary hover:bg-bg-secondary rounded-xs touch-manipulation",
            )}
            aria-label={t("editImage")}
          >
            <Edit2 size={16} variant="Bold" />
          </button>
        )}

        {/* Delete button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete();
          }}
          className={cn(
            "inline-flex items-center justify-center size-7 text-text-primary",
            "transition-all duration-200 bg-semantic-error rounded-xs touch-manipulation",
          )}
          aria-label={t("deleteFile")}
        >
          <XIcon size={20} />
        </button>
      </div>

      <p className="text-text-secondary line-clamp-1 text-xs text-center truncate max-w-[170px]">
        {file.name}
      </p>
    </div>
  );
};
